/**
 * Pure rules for the markers (owner B5). No MapLibre, no DOM: unit-tested.
 *
 *   tierFor(z, prev)       zoom tier with ±0.15 hysteresis around 10, 11 and 13 (spec §D)
 *   computePins(…)         which places are glass pins, in order (selection first, then nearest)
 *   labelIds(…)            which dots carry a text label (§D.1a)
 *   pinAriaLabel, pillParts, sizes by layout, cluster radii, nearest hit
 */
import type { CategoryKind, Place } from "@/contracts/data";
import { displayTravel } from "@/contracts/data";
import type { GroupId } from "@/contracts/data";
import type { Layout } from "@/contracts/store";
import { minutesText } from "@/features/a11y/announceText";
import { fmtMin } from "@/features/card/format";

/* ─── Zoom tiers ─────────────────────────────────────────────────────────── */

/** 0: < 10 clusters · 1: 10–11 group dots · 2: 11–13 pins for the focus set · 3: ≥ 13 pins with pills. */
export type Tier = 0 | 1 | 2 | 3;
export const TIER_BOUNDS = [10, 11, 13] as const;
export const TIER_HYSTERESIS = 0.15;

export function rawTier(z: number): Tier {
  if (z >= TIER_BOUNDS[2]) return 3;
  if (z >= TIER_BOUNDS[1]) return 2;
  if (z >= TIER_BOUNDS[0]) return 1;
  return 0;
}

/** Up needs z ≥ b + 0.15, down needs z < b − 0.15; with no previous tier, the plain boundaries apply. */
export function tierFor(z: number, prev: Tier | null): Tier {
  if (prev === null || !Number.isFinite(z)) return rawTier(Number.isFinite(z) ? z : 0);
  let t: number = prev;
  while (t < 3 && z >= TIER_BOUNDS[t] + TIER_HYSTERESIS) t++;
  while (t > 0 && z < TIER_BOUNDS[t - 1] - TIER_HYSTERESIS) t--;
  return t as Tier;
}

/* ─── Places as the markers see them ─────────────────────────────────────── */

export interface MarkerPlace {
  id: string;
  name: string;
  group: GroupId;
  category: string;
  kind: CategoryKind;
  minutes: number | null; //  display minutes (overrides win), null without a route and for future corridors
  icon: string;
  coord: [number, number];
  isSite: boolean;
  extra: Array<{ coord: [number, number]; note: string }>;
}

/** Road and future places have no point mark (their corridor is the mark); the site has the Whorl. */
export const hasDot = (p: Pick<MarkerPlace, "kind" | "isSite">): boolean => !p.isSite && p.kind !== "road" && p.kind !== "future";

export function toMarkerPlaces(places: Place[], categories: Array<{ id: string; kind: CategoryKind; icon: string }>): MarkerPlace[] {
  const cats = new Map(categories.map((c) => [c.id, c]));
  return places.map((p) => {
    const c = cats.get(p.category);
    const kind = c?.kind ?? "poi";
    return {
      id: p.id,
      name: p.name,
      group: p.group,
      category: p.category,
      kind,
      // Future corridors have no drive time on any surface (§B.1), spoken or shown; nor has the site.
      minutes: kind === "future" || p.isSite ? null : (displayTravel(p)?.minutes ?? null),
      icon: p.icon ?? c?.icon ?? "mapPin",
      coord: [p.coord[0], p.coord[1]],
      isSite: !!p.isSite,
      extra: p.extraPins.map((x) => ({ coord: [x.coord[0], x.coord[1]] as [number, number], note: x.note })),
    };
  });
}

const byMinutes = (a: MarkerPlace, b: MarkerPlace): number =>
  (a.minutes ?? Number.POSITIVE_INFINITY) - (b.minutes ?? Number.POSITIVE_INFINITY) || a.name.localeCompare(b.name);

/* ─── Glass pins ─────────────────────────────────────────────────────────── */

export const MAX_PINS = 25;
/** Extra pins (e.g. a corridor's connection points) are keyed "{placeId}#{i}". */
export const extraKey = (placeId: string, i: number): string => `${placeId}#${i}`;
export const parseKey = (key: string): { placeId: string; extra: number | null } => {
  const i = key.lastIndexOf("#");
  return i < 0 ? { placeId: key, extra: null } : { placeId: key.slice(0, i), extra: Number(key.slice(i + 1)) };
};

export interface PinRuleInput {
  tier: Tier;
  selectedId: string | null;
  /** setFocusSet(ids): these are pins at every zoom. null: the zoom rules (the store's category from z11). */
  focus: string[] | null;
  category: string | null;
  places: ReadonlyMap<string, MarkerPlace>;
  max?: number;
}

/**
 * Pin keys in display order: the selection, its extra pins, then the focus set nearest first.
 * - an explicit focus set shows at every tier (bloom pins arrive at region zooms too);
 * - with no explicit focus set, the active category's places become pins from tier 2 (z ≥ 11), except
 *   road and future places, whose corridor is their mark;
 * - never the site; at most `max` (25).
 */
export function computePins(o: PinRuleInput): string[] {
  const max = o.max ?? MAX_PINS;
  const out: string[] = [];
  const seen = new Set<string>();
  const push = (k: string) => {
    if (out.length < max && !seen.has(k)) {
      seen.add(k);
      out.push(k);
    }
  };
  const sel = o.selectedId ? o.places.get(o.selectedId) : undefined;
  if (sel && !sel.isSite) {
    push(sel.id);
    sel.extra.forEach((_, i) => push(extraKey(sel.id, i)));
  }
  let focus: MarkerPlace[] = [];
  if (o.focus) {
    focus = o.focus.map((id) => o.places.get(id)).filter((p): p is MarkerPlace => !!p && !p.isSite);
  } else if (o.tier >= 2 && o.category) {
    for (const p of o.places.values()) if (p.category === o.category && hasDot(p)) focus.push(p);
  }
  focus.sort(byMinutes);
  for (const p of focus) push(p.id);
  return out;
}

/* ─── Labels (§D.1a) ─────────────────────────────────────────────────────── */

export interface LabelRuleInput {
  tier: Tier;
  group: GroupId;
  category: string | null;
  selectedId: string | null;
  pinned: ReadonlySet<string>;
  places: Iterable<MarkerPlace>;
}

/**
 * z < 11: none. 11–13: the active category's places if a chip is pressed, otherwise the active group's;
 * while a place is selected only the active category's (none with "All"). z ≥ 13: all (they collide).
 * Never on a glass pin, a road or future place, or the site.
 */
export function labelIds(o: LabelRuleInput): string[] {
  if (o.tier < 2) return [];
  const out: string[] = [];
  for (const p of o.places) {
    if (!hasDot(p) || o.pinned.has(p.id)) continue;
    if (o.tier >= 3) out.push(p.id);
    else if (o.category) {
      if (p.category === o.category) out.push(p.id);
    } else if (!o.selectedId && p.group === o.group) out.push(p.id);
  }
  return out;
}

/* ─── Text ───────────────────────────────────────────────────────────────── */

/** "{name}, {min} minutes by road, off-peak" (§H.2; drive times are always off-peak): "2 hours 1 minute". */
export function pinAriaLabel(name: string, minutes: number | null): string {
  if (minutes == null) return name;
  return `${name}, ${minutesText(minutes)} by road, off-peak`;
}

/** The pill: name plus "{min} min off-peak" in the quieter weight, minutes as §B.1 ("2 h 1 min"). */
export function pillParts(name: string, minutes: number | null): { name: string; meta: string } {
  return { name, meta: minutes == null ? "" : `${fmtMin(minutes)} off-peak` };
}

/* ─── Sizes by layout (§D.1, §D.3, §D.4, §D.12, §E.1) ───────────────────── */

export interface PinSizes {
  hit: number;
  disc: number;
  icon: number;
  /** The pill's inner edge from the pin centre (hit / 2 + 4) and its text size. */
  pillGap: number;
  pillText: number;
}
export function pinSizes(layout: Layout): PinSizes {
  if (layout === "kiosk") return { hit: 56, disc: 40, icon: 20, pillGap: 32, pillText: 14.4 };
  if (layout === "mobile") return { hit: 44, disc: 32, icon: 16, pillGap: 26, pillText: 12 };
  return { hit: 40, disc: 32, icon: 16, pillGap: 24, pillText: 12 };
}

/** Dot hit tolerance: half of each layout's minimum target (§D.1). */
export const hitTolerance = (layout: Layout): number => (layout === "kiosk" ? 28 : layout === "mobile" ? 22 : 8);
export const siteMarkSize = (layout: Layout): number => (layout === "kiosk" ? 56 : layout === "mobile" ? 36 : 44);
export const pulseRadius = (layout: Layout): number => (layout === "kiosk" ? 72 : 56);
/** Map text factor and extra halo (§D.12). */
export const textScale = (layout: Layout): { k: number; halo: number } => (layout === "kiosk" ? { k: 1.2, halo: 0.5 } : { k: 1, halo: 0 });

/** Cluster core radius by point count (§D.2): 2–4 → 13, 5–9 → 16, 10–24 → 19, ≥ 25 → 22. */
export function clusterRadius(count: number): number {
  if (count >= 25) return 22;
  if (count >= 10) return 19;
  if (count >= 5) return 16;
  return 13;
}

/* ─── Hit testing ────────────────────────────────────────────────────────── */

export interface Candidate<T> {
  item: T;
  x: number;
  y: number;
  /** Lower wins a tie at the same distance (dots before clusters before corridors). */
  rank?: number;
}

/** The candidate nearest to the pointer (§D.1: "the nearest feature to the pointer wins"). */
export function nearest<T>(cands: Candidate<T>[], px: number, py: number): T | null {
  let best: Candidate<T> | null = null;
  let bd = Infinity;
  for (const c of cands) {
    const d = Math.hypot(c.x - px, c.y - py) + (c.rank ?? 0) * 0.001;
    if (d < bd) {
      bd = d;
      best = c;
    }
  }
  return best ? best.item : null;
}

/** Whether a pill of `pillW` px starting `gap` px right of a pin at screen x crosses the right inset. */
export function pillFlips(x: number, gap: number, pillW: number, viewportW: number, insetRight: number): boolean {
  return x + gap + pillW > viewportW - insetRight;
}

/** The narrowest a clamped pill may get (two short lines); below it the pill keeps its natural width. */
export const PILL_MIN_CLAMP = 96;

/**
 * The side a pill takes (§D.3: pills stay readable in the safe frame). Right of the pin when it fits
 * before the right inset; else left when it fits after the left inset; else the side with more room,
 * with `max` (px) so the pill wraps inside it rather than running under a panel. `max` is null when the
 * pill fits, or when even the roomier side is narrower than PILL_MIN_CLAMP.
 */
export function pillSide(x: number, gap: number, pillW: number, viewportW: number, insets: { left: number; right: number }): { flip: boolean; max: number | null } {
  const roomR = viewportW - insets.right - (x + gap);
  const roomL = x - gap - insets.left;
  if (pillW <= roomR) return { flip: false, max: null };
  if (pillW <= roomL) return { flip: true, max: null };
  const flip = roomL > roomR;
  const room = Math.floor(Math.max(roomL, roomR));
  return { flip, max: room >= PILL_MIN_CLAMP ? room : null };
}

/* ─── The site's label box (§D.4) ───────────────────────────────────────── */

/**
 * Whether a bloom pin gives way to the site's name (L1-02): below z13 a pin whose disc touches the
 * site mark or its label box shows as its group dot instead (its route is still drawn). Never the
 * selection, a hovered pin or an extra pin.
 */
export function tucksUnderSite(pin: { x: number; y: number }, disc: number, site: Rect[], o: { zoom: number; bloom: boolean; selected: boolean; hot: boolean; extra: boolean }): boolean {
  if (!o.bloom || o.selected || o.hot || o.extra || !(o.zoom < 13)) return false;
  const h = disc / 2;
  const r: Rect = { x0: pin.x - h, y0: pin.y - h, x1: pin.x + h, y1: pin.y + h };
  return site.some((q) => overlaps(q, r));
}

/* ─── Persistent pills (z ≥ 13) ─────────────────────────────────────────── */

export interface Rect {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}
const overlaps = (a: Rect, b: Rect): boolean => a.x0 < b.x1 && b.x0 < a.x1 && a.y0 < b.y1 && b.y0 < a.y1;

export interface PillPin {
  x: number; //  screen centre
  y: number;
  w: number; //  pill size
  h: number;
  want: boolean; // the rules ask for a persistent pill here
}

/**
 * Where each persistent pill goes, in priority order (the selection first, then nearest first, like
 * the labels' symbol-sort-key): right of the pin, or left when the right would cross the right inset
 * (§D.3). A pill that would cover another pin, a pill already placed or an obstacle (the site mark)
 * tries the other side, and otherwise waits for hover or focus. Nothing is placed under the panels.
 */
export function layoutPills(
  pins: PillPin[],
  o: { gap: number; hit: number; viewW: number; viewH: number; insets: { left: number; right: number }; obstacles?: Rect[] },
): Array<{ show: boolean; flip: boolean; max?: number }> {
  const half = o.hit / 2;
  const discs: Rect[] = pins.map((p) => ({ x0: p.x - half, y0: p.y - half, x1: p.x + half, y1: p.y + half }));
  const placed: Rect[] = [];
  const obstacles = o.obstacles ?? [];
  const minX = o.insets.left;
  const maxX = o.viewW - o.insets.right;
  return pins.map((p, i) => {
    // hover / focus pills (and the fallback for persistent ones) take the side with room (L4-11)
    const side = pillSide(p.x, o.gap, p.w, o.viewW, o.insets);
    const prefersLeft = side.flip;
    const rest = side.max !== null ? { show: false, flip: prefersLeft, max: side.max } : { show: false, flip: prefersLeft };
    if (!p.want || p.x < 0 || p.x > o.viewW || p.y < 0 || p.y > o.viewH) return rest;
    const y0 = p.y - p.h / 2;
    const y1 = p.y + p.h / 2;
    const right: Rect = { x0: p.x + o.gap, x1: p.x + o.gap + p.w, y0, y1 };
    const left: Rect = { x0: p.x - o.gap - p.w, x1: p.x - o.gap, y0, y1 };
    const sides: Array<[Rect, boolean]> = prefersLeft
      ? [
          [left, true],
          [right, false],
        ]
      : [
          [right, false],
          [left, true],
        ];
    for (const [r, flip] of sides) {
      if (r.x1 > maxX || r.x0 < minX) continue;
      if (discs.some((d, j) => j !== i && overlaps(d, r))) continue;
      if (placed.some((q) => overlaps(q, r))) continue;
      if (obstacles.some((q) => overlaps(q, r))) continue;
      placed.push(r);
      return { show: true, flip };
    }
    return rest;
  });
}
